import { useMemo } from "react";
import { useDailySalesQuery } from "../../query/adminDailySalesQuery";
import {
  getMoneyForCurrency,
  isSalesToday,
} from "../../utils/dailySalesUtils";
import { formatMoney } from "../../utils/formatMoney";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { SalesPieChartCard } from "./SalesPieChartCard";
import { useTranslation } from "react-i18next";

interface SalesReceiptTypePieChartProps {
  resellerId?: string;
  date: string;
  selectedDateLabel: string;
}

export const SalesReceiptTypePieChart = ({
  resellerId,
  date,
  selectedDateLabel,
}: SalesReceiptTypePieChartProps) => {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useDailySalesQuery(date, date, resellerId);
  const isToday = isSalesToday(date);

  const chartData = useMemo(() => {
    const dayStats = stats?.[0];
    if (!dayStats?.grossAmountByReceiptTypeByCurrency) return [];

    return Object.entries(dayStats.grossAmountByReceiptTypeByCurrency)
      .map(([type, byCurrency]) => {
        const money = getMoneyForCurrency(byCurrency, Currency.SEK);
        return { type, money };
      })
      .filter((entry) => entry.money && entry.money.amount > 0)
      .map((entry) => ({
        key: entry.type,
        name: t(`statistics.receiptType.${entry.type}`, entry.type),
        value: entry.money!.amount,
        label: formatMoney(entry.money),
      }));
  }, [stats, t]);

  const chartTitle = isToday
    ? t("adminDailySales.salesByReceiptTypeToday")
    : t("adminDailySales.salesByReceiptTypeOnDate", {
        date: selectedDateLabel,
      });

  const emptyMessage = isToday
    ? t("adminDailySales.noDataToday")
    : t("adminDailySales.noDataOnDate", { date: selectedDateLabel });

  return (
    <SalesPieChartCard
      title={chartTitle}
      emptyMessage={emptyMessage}
      isLoading={isLoading}
      chartData={chartData}
    />
  );
};
